import { useState } from 'react';
import { toast } from '@cencosud-cencommerce/paris-uikit';

import { useRepository } from '@/Application/Providers/Repository';
import { saveShippingAddress } from '@/modules/cart/application/saveShippingAddress';
import type { Address } from '@/modules/cart/domain/Address';
import { SendShippingEvents } from '@/modules/shared/utils/gtmEvents';
import { ShippingEventInteractiveActions } from '@/modules/shared/utils/gtmEvents/types';
import { UseFindLocation } from '@/sections/address/hooks/useFindLocation';
import { usePlaceData } from '@/sections/region/GooglePlaceDataContext';
import type { MutatorHook } from '@/sections/shared/types/MutatorHook';

type Entity = Address | Omit<Address, 'id'>;

const useCreateShippingAddress = (
  showInfoMessages = true
): MutatorHook<Entity> => {
  const { cart: cartRepository } = useRepository();
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const { find } = UseFindLocation();
  const { placeData, setPlaceData } = usePlaceData();

  const trigger = async (address: Entity) => {
    setIsLoading(true);
    const { selectedCommune, selectedRegion } = find(address);

    const sendResultEvent = (title: string) => {
      SendShippingEvents.sendEvent({
        event: 'baseEvent',
        extraFields: {
          'event-interactive-tag': title,
          'event-interactive-accion':
            ShippingEventInteractiveActions.AddAddress,
          'event-interactive-comuna': selectedCommune?.name,
          'event-interactive-region': selectedRegion?.name
        }
      });
    };

    try {
      if (placeData?.place?.address_formatted) {
        address.streetName = placeData?.place?.route;
        address.streetNumber = placeData?.place?.street_number;

        if (placeData?.place?.latitude && placeData?.place?.longitude) {
          address.latitude = placeData?.place?.latitude;
          address.lat = placeData?.place?.latitude;
          address.longitude = placeData?.place?.longitude;
          address.lon = placeData?.place?.longitude;
        }
      }

      if (address?.lat && address?.lon) {
        address.latitude = address?.lat;
        address.longitude = address?.lon;
        address.lat = undefined;
        address.lon = undefined;
      }

      if (address?.isAutocomplete === false) {
        address.latitude = undefined;
        address.longitude = undefined;
      }

      const streetNameUntilFirstComma = address?.streetName?.split(',')?.[0];
      address.streetName = streetNameUntilFirstComma || address?.streetName;

      const res = await saveShippingAddress(cartRepository, address);
      setPlaceData(null);

      if (!res) {
        throw new Error('Error al guardar la dirección');
      }

      if (showInfoMessages) {
        if ('id' in address) {
          const title = 'Actualizaste una dirección';
          sendResultEvent(title);
          toast({ title });
        } else {
          const title = 'Agregaste una dirección';
          sendResultEvent(title);
          toast({ title });
        }
      }
    } catch (e) {
      const title = 'Error al guardar la dirección';
      sendResultEvent(title);
      toast({
        status: 'error',
        title
      });
      setError((e as Error).message);
    } finally {
      setIsLoading(false);
    }
  };

  return { trigger, isLoading, error };
};

export default useCreateShippingAddress;
